import SimplePeer from 'simple-peer';
import { Socket } from 'socket.io-client';

export type CallState = 'idle' | 'calling' | 'ringing' | 'connected' | 'ended';
export type EndReason = 'rejected' | 'busy' | 'unavailable' | 'hangup' | 'error' | null;

export interface CallerInfo {
  userId: string;
  name: string;
  avatar?: string;
}

export interface CallCallbacks {
  onStateChange: (state: CallState) => void;
  onRemoteStream: (stream: MediaStream) => void;
  onIncomingCall: (callerInfo: CallerInfo) => void;
  onError: (err: Error) => void;
  onEndReason?: (reason: EndReason) => void;
}

// STUN: resolves public IP for cone NAT (home broadband, same-WiFi).
// TURN: required for carrier-grade / symmetric NAT (5G, LTE, corporate).
//   Media stays DTLS-SRTP encrypted end-to-end even through a TURN relay —
//   the relay server forwards encrypted packets and cannot read content.
// Built-in free TURN (openrelay.metered.ca) works for testing/small deployments.
// For production reliability set REACT_APP_TURN_URL / _USERNAME / _CREDENTIAL.
const ICE_SERVERS: RTCIceServer[] = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
  { urls: 'stun:stun2.l.google.com:19302' },
  // Free TURN fallback — covers symmetric NAT on mobile carriers
  { urls: 'turn:openrelay.metered.ca:80',               username: 'openrelayproject', credential: 'openrelayproject' },
  { urls: 'turn:openrelay.metered.ca:443',              username: 'openrelayproject', credential: 'openrelayproject' },
  { urls: 'turn:openrelay.metered.ca:443?transport=tcp', username: 'openrelayproject', credential: 'openrelayproject' },
  // Production TURN override
  ...(process.env.REACT_APP_TURN_URL
    ? [{ urls: process.env.REACT_APP_TURN_URL, username: process.env.REACT_APP_TURN_USERNAME || '', credential: process.env.REACT_APP_TURN_CREDENTIAL || '' }]
    : [])
];

// Use a dedicated event name to avoid any collision with the pre-existing
// messaging WebRTC signal event.
const VIDEO_SIGNAL_EVENT = 'video-signal';

// How long to wait for ICE to reach "connected" before giving up.
const CONNECTION_TIMEOUT_MS = 15_000;

export class VideoCallService {
  private socket: Socket | null = null;
  private peer: SimplePeer.Instance | null = null;
  private localStream: MediaStream | null = null;
  private callbacks: CallCallbacks | null = null;
  private currentUserId: string = '';
  private activePeerId: string = '';
  private state: CallState = 'idle';
  private pendingSignals: any[] = [];
  private endedTimer: ReturnType<typeof setTimeout> | null = null;
  private connectionTimer: ReturnType<typeof setTimeout> | null = null;
  private endReason: EndReason = null;

  // ── Named socket handlers (needed for clean .off()) ──────────────────────

  private _onCallRequest = ({ callerId, callerInfo }: { callerId: string; callerInfo: CallerInfo }) => {
    if (this.state !== 'idle') {
      this.socket?.emit('call-busy', { callerId });
      return;
    }
    this.activePeerId = String(callerId);
    this._setState('ringing');
    this.callbacks?.onIncomingCall(callerInfo);
  };

  private _onCallAccept = async ({ accepterId }: { accepterId: string }) => {
    this.activePeerId = String(accepterId);
    this._startConnectionTimeout();
    await this._startPeer(true);
  };

  private _onCallReject = () => { this._endCall('rejected'); };
  private _onCallEnd    = () => { this._endCall('hangup'); };
  private _onCallBusy   = () => { this._endCall('busy'); };
  private _onCallUnavailable = () => { this._endCall('unavailable'); };

  private _onVideoSignal = ({ fromUserId, signal }: { fromUserId: string; signal: any }) => {
    if (String(fromUserId) !== this.activePeerId) return;
    if (this.peer) {
      try { this.peer.signal(signal); } catch (_) {}
    } else {
      this.pendingSignals.push(signal);
    }
  };

  // ── Public API ────────────────────────────────────────────────────────────

  init(socket: Socket, userId: string, callbacks: CallCallbacks) {
    this._unbindSocketEvents();
    this.socket = socket;
    this.currentUserId = userId;
    this.callbacks = callbacks;
    this._bindSocketEvents();

    // Re-bind if the socket reconnects mid-call
    socket.on('reconnect', () => {
      this._unbindSocketEvents();
      this._bindSocketEvents();
      // If we were in a call, signal was lost — clean up
      if (this.state !== 'idle' && this.state !== 'ended') {
        this._endCall();
      }
    });
  }

  async initiateCall(targetUserId: string, callerInfo: CallerInfo) {
    if (this.state !== 'idle') return;
    this.activePeerId = String(targetUserId);
    this._setState('calling');
    this.socket?.emit('call-request', { targetUserId, callerInfo });
  }

  async acceptCall() {
    if (this.state !== 'ringing') return;
    this._startConnectionTimeout();
    await this._startPeer(false);
    this.socket?.emit('call-accept', { callerId: this.activePeerId });
  }

  rejectCall() {
    if (this.state !== 'ringing') return;
    this.socket?.emit('call-reject', { callerId: this.activePeerId });
    this._peerCleanup();
    this._setState('idle');
  }

  hangUp() {
    const peerId = this.activePeerId;
    this._peerCleanup();
    this.endReason = 'hangup';
    this.callbacks?.onEndReason?.('hangup');
    this._setState('ended');
    if (peerId) this.socket?.emit('call-end', { peerId });
    this._scheduleIdle();
  }

  getLocalStream(): MediaStream | null { return this.localStream; }
  getState(): CallState { return this.state; }
  getEndReason(): EndReason { return this.endReason; }

  destroy() {
    if (this.endedTimer) clearTimeout(this.endedTimer);
    if (this.connectionTimer) clearTimeout(this.connectionTimer);
    this._unbindSocketEvents();
    this._peerCleanup();
    this.callbacks = null;
  }

  // ── Internal ──────────────────────────────────────────────────────────────

  private _setState(s: CallState) {
    this.state = s;
    this.callbacks?.onStateChange(s);
  }

  private _scheduleIdle() {
    if (this.endedTimer) clearTimeout(this.endedTimer);
    this.endedTimer = setTimeout(() => {
      this.endedTimer = null;
      this.endReason = null;
      this.callbacks?.onEndReason?.(null);
      this._setState('idle');
    }, 2000);
  }

  private _endCall(reason: EndReason = 'hangup') {
    this._cancelConnectionTimeout();
    this._peerCleanup();
    this.endReason = reason;
    this.callbacks?.onEndReason?.(reason);
    this._setState('ended');
    this._scheduleIdle();
  }

  private _startConnectionTimeout() {
    this._cancelConnectionTimeout();
    this.connectionTimer = setTimeout(() => {
      if (this.state !== 'connected') {
        this.callbacks?.onError(new Error('Call timed out — could not establish connection'));
        this.hangUp();
      }
    }, CONNECTION_TIMEOUT_MS);
  }

  private _cancelConnectionTimeout() {
    if (this.connectionTimer) {
      clearTimeout(this.connectionTimer);
      this.connectionTimer = null;
    }
  }

  private _bindSocketEvents() {
    if (!this.socket) return;
    this.socket.on('call-request',     this._onCallRequest);
    this.socket.on('call-accept',      this._onCallAccept);
    this.socket.on('call-reject',      this._onCallReject);
    this.socket.on('call-end',         this._onCallEnd);
    this.socket.on('call-busy',        this._onCallBusy);
    this.socket.on('call-unavailable', this._onCallUnavailable);
    this.socket.on(VIDEO_SIGNAL_EVENT, this._onVideoSignal);
  }

  private _unbindSocketEvents() {
    if (!this.socket) return;
    this.socket.off('call-request',     this._onCallRequest);
    this.socket.off('call-accept',      this._onCallAccept);
    this.socket.off('call-reject',      this._onCallReject);
    this.socket.off('call-end',         this._onCallEnd);
    this.socket.off('call-busy',        this._onCallBusy);
    this.socket.off('call-unavailable', this._onCallUnavailable);
    this.socket.off(VIDEO_SIGNAL_EVENT, this._onVideoSignal);
  }

  private async _startPeer(initiator: boolean) {
    // Guard: mediaDevices is undefined on HTTP or in restricted contexts
    if (!navigator.mediaDevices?.getUserMedia) {
      this.callbacks?.onError(new Error('Media devices unavailable — site must be served over HTTPS'));
      this._cancelConnectionTimeout();
      this._peerCleanup();
      this.endReason = 'error';
      this.callbacks?.onEndReason?.('error');
      this._setState('ended');
      this._scheduleIdle();
      return;
    }

    // On Android PWA, permissions may not be pre-granted. Query first so the
    // browser shows the native permission dialog before we start the peer.
    if (navigator.permissions?.query) {
      try {
        const [cam, mic] = await Promise.all([
          navigator.permissions.query({ name: 'camera' as PermissionName }),
          navigator.permissions.query({ name: 'microphone' as PermissionName }),
        ]);
        // If explicitly denied (not just "prompt"), bail early with clear message
        if (mic.state === 'denied') {
          this.callbacks?.onError(new Error('Microphone permission denied — enable it in browser/app settings'));
          this._cancelConnectionTimeout(); this._peerCleanup();
          this.endReason = 'error'; this.callbacks?.onEndReason?.('error');
          this._setState('ended'); this._scheduleIdle(); return;
        }
      } catch (_) { /* permissions API not supported — proceed */ }
    }

    // Try video+audio first, then audio-only fallback (camera may be unavailable
    // or denied while mic is allowed)
    try {
      this.localStream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
    } catch (videoErr: any) {
      try {
        this.localStream = await navigator.mediaDevices.getUserMedia({ video: false, audio: true });
      } catch (audioErr: any) {
        const name = (audioErr?.name || videoErr?.name || '');
        const msg = name === 'NotAllowedError'
          ? 'Microphone permission denied — tap Allow when the browser asks, or enable in settings'
          : name === 'NotFoundError'
          ? 'No microphone found on this device'
          : `Could not access microphone (${name || 'unknown error'})`;
        this.callbacks?.onError(new Error(msg));
        this._cancelConnectionTimeout();
        this._peerCleanup();
        this.endReason = 'error';
        this.callbacks?.onEndReason?.('error');
        this._setState('ended');
        this._scheduleIdle();
        return;
      }
    }

    this.peer = new SimplePeer({
      initiator,
      stream: this.localStream,
      trickle: true,
      config: { iceServers: ICE_SERVERS }
    });

    // Flush signals that arrived before the peer was ready
    while (this.pendingSignals.length > 0) {
      const sig = this.pendingSignals.shift();
      try { this.peer.signal(sig); } catch (_) {}
    }

    this.peer.on('signal', (data) => {
      this.socket?.emit(VIDEO_SIGNAL_EVENT, { targetUserId: this.activePeerId, signal: data });
    });

    this.peer.on('stream', (stream) => {
      this._cancelConnectionTimeout();
      this._setState('connected');
      this.callbacks?.onRemoteStream(stream);
    });

    // Watch the underlying RTCPeerConnection ICE state for silent failures
    const pc = (this.peer as any)._pc as RTCPeerConnection | undefined;
    if (pc) {
      pc.oniceconnectionstatechange = () => {
        if (pc.iceConnectionState === 'failed') {
          // Attempt ICE restart before giving up
          if (initiator) {
            try { pc.restartIce(); } catch (_) {}
          }
        }
        if (pc.iceConnectionState === 'disconnected') {
          // Transient — wait for the connection timeout to decide if it's permanent
        }
      };
    }

    this.peer.on('error', (err) => {
      this.callbacks?.onError(err);
      this._endCall('error');
    });

    this.peer.on('close', () => {
      if (this.state === 'connected' || this.state === 'calling' || this.state === 'ringing') {
        this._endCall();
      }
    });
  }

  private _peerCleanup() {
    this._cancelConnectionTimeout();
    this.peer?.destroy();
    this.peer = null;
    this.localStream?.getTracks().forEach(t => t.stop());
    this.localStream = null;
    this.activePeerId = '';
    this.pendingSignals = [];
  }
}

export const videoCallService = new VideoCallService();
